import { useState } from 'react';
import { useAppStore } from '@/store/AppStore';
import Badge from '@/components/base/Badge';
import EmptyState from '@/components/base/EmptyState';
import CustomerDetail from './components/CustomerDetail';
import CustomerForm from './components/CustomerForm';
import type { Customer } from '@/types';

export default function Musteriler() {
  const { customers } = useAppStore();
  const [selected, setSelected] = useState<Customer | null>(null);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const filtered = customers.filter(
    (c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search),
  );

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            Müşteri
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Müşteriler</h1>
          <p className="mt-1 text-sm text-foreground-500">Tüm müşteri yolculuğunu tek yerden izleyin.</p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          <i className="ri-add-line" />
          Yeni Müşteri
        </button>
      </div>

      <input
        className="h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none sm:w-72"
        placeholder="Müşteri veya telefon ara..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-user-line"
            title="Müşteri kaydı bulunmuyor."
            description="Arama kriterlerini değiştirin veya yeni müşteri ekleyin."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-background-100 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  <th className="px-5 py-3">Müşteri</th>
                  <th className="px-5 py-3">Telefon</th>
                  <th className="px-5 py-3">Bölge</th>
                  <th className="px-5 py-3">Son Hizmet</th>
                  <th className="px-5 py-3">Toplam İş</th>
                  <th className="px-5 py-3">Son İletişim</th>
                  <th className="px-5 py-3">Durum</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelected(c)}
                    className="cursor-pointer border-b border-background-100 transition-colors hover:bg-background-50"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                          {c.name.split(' ').map((w) => w[0]).join('')}
                        </span>
                        <span className="font-semibold text-foreground-900">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-foreground-600">{c.phone}</td>
                    <td className="px-5 py-3.5 text-foreground-600">{c.region}</td>
                    <td className="px-5 py-3.5 text-foreground-500">{c.lastService}</td>
                    <td className="px-5 py-3.5 text-foreground-600">{c.totalJobs}</td>
                    <td className="px-5 py-3.5 text-foreground-500">{c.lastContact}</td>
                    <td className="px-5 py-3.5">
                      <Badge tone={c.status === 'Aktif' ? 'green' : c.status === 'Pasif' ? 'red' : 'neutral'}>{c.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <CustomerDetail customer={selected} onClose={() => setSelected(null)} />}
      {formOpen && <CustomerForm open={formOpen} onClose={() => setFormOpen(false)} />}
    </div>
  );
}